import React from 'react';

import { useTranslation } from 'react-i18next';

import { Icon, Tooltip } from 'design-system-web';
import {
  FONT_AWESOME_DUOTONE,
  ICON_SET_FONTAWESOME,
} from 'lane-shared/helpers/constants/icons';

import {
  TargetDueUiState,
  formatTargetDuePrimaryLabel,
  getTargetDueUiState,
  withTargetPrefix,
} from './targetDueCellFormatting';

import styles from './styles.scss';

export type TargetDueTimeCellKind = 'response' | 'completion';

export type TargetDueTimeCellProps = {
  /** GraphQL may supply an ISO string at runtime */
  dueAt: Date | string | undefined;
  targetMet: boolean | null | undefined;
  timeZone: string;
  kind: TargetDueTimeCellKind;
  /** For tests only */
  nowMs?: number;
};

const TOOLTIP_KEYS = {
  response: {
    [TargetDueUiState.Met]: 'web.admin.serviceRequest.targetResponseTime.tooltipMet',
    [TargetDueUiState.PastDue]: 'web.admin.serviceRequest.targetResponseTime.tooltipUnmet',
  },
  completion: {
    [TargetDueUiState.Met]: 'web.admin.serviceRequest.targetCompletionTime.tooltipMet',
    [TargetDueUiState.PastDue]: 'web.admin.serviceRequest.targetCompletionTime.tooltipUnmet',
  },
} as const;

export function TargetDueTimeCell({
  dueAt,
  targetMet,
  timeZone,
  kind,
  nowMs = Date.now(),
}: TargetDueTimeCellProps) {
  const { t } = useTranslation();

  if (!dueAt) {
    return null;
  }

  const state = getTargetDueUiState(dueAt, targetMet, nowMs);
  const label = formatTargetDuePrimaryLabel(dueAt, timeZone, nowMs);

  if (!label) {
    return null;
  }

  const displayText =
    state === TargetDueUiState.Future ? withTargetPrefix(label) : label;

  const cellClassName = [
    styles.targetDueCell,
    state === TargetDueUiState.Met ? styles.targetDueCellMet : '',
    state === TargetDueUiState.PastDue ? styles.targetDueCellPastDue : '',
  ]
    .filter(Boolean)
    .join(' ');

  let iconName: 'calendar-check' | 'calendar-exclamation' | null = null;

  if (state === TargetDueUiState.Met) {
    iconName = 'calendar-check';
  } else if (state === TargetDueUiState.PastDue) {
    iconName = 'calendar-exclamation';
  }

  const cellContent = (
    <span className={cellClassName}>
      {iconName ? (
        <Icon
          name={iconName}
          set={ICON_SET_FONTAWESOME}
          type={FONT_AWESOME_DUOTONE}
          className={styles.targetDueCellIcon}
        />
      ) : null}
      <span>{displayText}</span>
    </span>
  );

  if (state === TargetDueUiState.Future) {
    return cellContent;
  }

  return (
    <Tooltip placement="top" TooltipComponent={t(TOOLTIP_KEYS[kind][state])}>
      {cellContent}
    </Tooltip>
  );
}
